import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, ProgressBar, Alert, Spinner, Form } from 'react-bootstrap';
import { jwtDecode } from "jwt-decode";
import axiosInstance from "../../api/axiosConfig.jsx";

const CourseView = () => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const [course, setCourse] = useState(null);
    const [lessons, setLessons] = useState([]);
    const [currentLesson, setCurrentLesson] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [lessonMaterials, setLessonMaterials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [openAnswers, setOpenAnswers] = useState({});
    const [answerFeedback, setAnswerFeedback] = useState({});
    const [isLastLesson, setIsLastLesson] = useState(false);

    const fetchQuestions = async (lessonId) => {
        console.log(`Fetching questions for lesson ID: ${lessonId}`);
        try {
            const response = await axiosInstance.get(`/questions/lessons/${lessonId}`);
            console.log('Fetched questions:', response.data);
            const validQuestions = response.data.map((question) => ({
                ...question,
                answers: question.answers || [],
            }));
            setQuestions(validQuestions);
        } catch (err) {
            console.error('Error fetching questions:', err);
            setError('Er is een fout opgetreden bij het laden van de vragen.');
            setQuestions([]);
        }
    };

    const fetchLessonMaterials = async (lessonId) => {
        console.log(`Fetching materials for lesson ID: ${lessonId}`);
        try {
            const response = await axiosInstance.get(`/api/lesson-materials/lesson/${lessonId}`);
            console.log('Fetched lesson materials:', response.data);
            setLessonMaterials(response.data);
        } catch (err) {
            console.error('Error fetching lesson materials:', err);
            setError('Er is een fout opgetreden bij het laden van de lesmaterialen.');
            setLessonMaterials([]);
        }
    };

    const fetchInitialData = async () => {
        try {
            const authToken = sessionStorage.getItem('authToken');
            if (!authToken) {
                throw new Error('Geen authToken gevonden in sessionStorage.');
            }

            const decodedToken = jwtDecode(authToken);
            const userId = decodedToken.userId;

            const [courseResponse, lessonsResponse] = await Promise.all([
                axiosInstance.get(`/courses/${courseId}`),
                axiosInstance.get(`/lessons/course/${courseId}`),
            ]);

            console.log('Course data:', courseResponse.data);
            console.log('Lessons data:', lessonsResponse.data);

            setCourse(courseResponse.data);
            setLessons(lessonsResponse.data);

            const progressResponse = await axiosInstance.get(`/progress/${userId}/${courseId}`);

            if (progressResponse.data) {
                console.log('Progress data:', progressResponse.data);
                const currentLessonId = progressResponse.data.currentLessonId;
                const lessonIndex = lessonsResponse.data.findIndex(
                    (lesson) => lesson.lessonId === currentLessonId
                );
                setProgress(((lessonIndex + 1) / lessonsResponse.data.length) * 100);

                const currentLesson = lessonsResponse.data.find(
                    (lesson) => lesson.lessonId === currentLessonId
                );

                if (currentLesson) {
                    setCurrentLesson(currentLesson);
                    // Check if this is the last lesson
                    setIsLastLesson(lessonIndex === lessonsResponse.data.length - 1);
                    // Pass the lessonId from the current lesson
                    await Promise.all([
                        fetchQuestions(currentLesson.lessonId),
                        fetchLessonMaterials(currentLesson.lessonId)
                    ]);
                } else {
                    setCurrentLesson(lessonsResponse.data[0]);
                    setIsLastLesson(lessonsResponse.data.length === 1);
                    // Pass the lessonId from the first lesson
                    await Promise.all([
                        fetchQuestions(lessonsResponse.data[0].lessonId),
                        fetchLessonMaterials(lessonsResponse.data[0].lessonId)
                    ]);
                }
            } else {
                const firstLesson = lessonsResponse.data[0];
                if (firstLesson) {
                    await axiosInstance.post('/progress', {
                        userId,
                        courseId: parseInt(courseId),
                        currentLessonId: firstLesson.lessonId,
                    });
                    setCurrentLesson(firstLesson);
                    setProgress(0);
                    setIsLastLesson(lessonsResponse.data.length === 1);

                    // Pass the lessonId from the first lesson
                    await Promise.all([
                        fetchQuestions(firstLesson.lessonId),
                        fetchLessonMaterials(firstLesson.lessonId)
                    ]);
                }
            }
            setLoading(false);
        } catch (err) {
            setError('Er is een fout opgetreden bij het laden van de cursus.');
            console.error('Fetch error:', err);
            setLoading(false);
        }
    };

    useEffect(() => {
        console.log('Fetching initial data for course:', courseId);
        fetchInitialData();
    }, [courseId]);

    const handleLessonChange = async (lesson) => {
        console.log(`Changing to lesson: ${lesson.title}`);
        const authToken = sessionStorage.getItem('authToken');
        if (!authToken) {
            throw new Error('Geen authToken gevonden in sessionStorage.');
        }

        const decodedToken = jwtDecode(authToken);
        const userId = decodedToken.userId;

        try {
            // De PUT-aanroep met currentLessonId als queryparameter
            await axiosInstance.put(`/progress/${userId}/${courseId}?currentLessonId=${lesson.lessonId}`);

            // Fetch vragen en materialen voor de nieuwe les
            await Promise.all([
                fetchQuestions(lesson.lessonId),
                fetchLessonMaterials(lesson.lessonId)
            ]);

            setCurrentLesson(lesson);
            const currentIndex = lessons.findIndex((l) => l.lessonId === lesson.lessonId);
            setProgress(((currentIndex + 1) / lessons.length) * 100);
            setIsLastLesson(currentIndex === lessons.length - 1);

            setSelectedAnswers({});
            setOpenAnswers({});
            setAnswerFeedback({});
            console.log('Form reset: answers and feedback cleared');
        } catch (err) {
            console.error('Error changing lesson:', err);
            setError('Er is een fout opgetreden bij het wijzigen van de les.');
        }
    };

    const handlePreviousLesson = async () => {
        console.log('Teruggaan naar vorige les');

        const currentIndex = lessons.findIndex((lesson) => lesson.lessonId === currentLesson.lessonId);

        if (currentIndex > 0) {
            const previousLesson = lessons[currentIndex - 1];

            if (previousLesson) {
                console.log(`Terug naar vorige les: ${previousLesson.title}`);
                await handleLessonChange(previousLesson);
            }
        }
    };



    const handleAnswerSelection = (questionId, answerId, isCorrect) => {
        console.log(`Selected answer for question ${questionId}: answer ${answerId} (correct: ${isCorrect})`);
        setSelectedAnswers((prevState) => ({
            ...prevState,
            [questionId]: answerId,
        }));
        setAnswerFeedback((prevState) => ({
            ...prevState,
            [questionId]: isCorrect ? 'Correct!' : 'Incorrect, probeer het opnieuw.',
        }));
    };

    const handleOpenAnswerChange = (questionId, answerText) => {
        console.log(`Open answer changed for question ${questionId}: ${answerText}`);
        setOpenAnswers((prevState) => ({
            ...prevState,
            [questionId]: answerText,
        }));
        const question = questions.find(q => q.questionId === questionId);
        if (question && question.correctOpenAnswer) {
            setAnswerFeedback((prevState) => ({
                ...prevState,
                [questionId]: answerText.trim().toLowerCase() === question.correctOpenAnswer.trim().toLowerCase()
                    ? 'Correct!'
                    : 'Incorrect, probeer het opnieuw.',
            }));
        }
    };

    const handleLessonComplete = async () => {
        console.log('Completing current lesson');
        const authToken = sessionStorage.getItem('authToken');
        if (!authToken) {
            throw new Error('Geen authToken gevonden in sessionStorage.');
        }

        const decodedToken = jwtDecode(authToken);
        const userId = decodedToken.userId;

        try {
            const currentIndex = lessons.findIndex((lesson) => lesson.lessonId === currentLesson.lessonId);

            if (currentIndex === -1) {
                console.error("Huidige les niet gevonden in lessenlijst.");
                return;
            }

            if (currentIndex === lessons.length - 1) {
                // Dit is de laatste les, cursus voltooien
                console.log("Dit is de laatste les, cursus wordt voltooid.");

                // Stuur een verzoek om de cursus als voltooid te markeren
                await axiosInstance.put(`/progress/${userId}/${courseId}/complete`);

                // Stuur de gebruiker terug naar het cursusoverzicht
                navigate('/courses');
            } else {
                // Ga naar de volgende les
                const nextLesson = lessons[currentIndex + 1];

                if (nextLesson) {
                    console.log(`Doorgaan naar volgende les: ${nextLesson.title}`);
                    await handleLessonChange(nextLesson);
                } else {
                    console.warn("Geen volgende les gevonden, maar er zouden meer moeten zijn.");
                }
            }
        } catch (err) {
            console.error('Error completing lesson:', err);
            setError('Er is een fout opgetreden bij het voltooien van de les.');
        }
    };



    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Laden...</span>
                </Spinner>
            </Container>
        );
    }

    if (!course || !currentLesson) return null;

    // Define a common style for scrollable content
    const scrollableStyle = {
        maxHeight: 'calc(100vh - 200px)', // Adjust based on your layout
        overflowY: 'auto',
        overflowX: 'hidden'
    };

    return (
        <Container fluid className="py-4">
            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}
            <Row>
                <Col md={6}>
                    <Card>
                        <Card.Header>
                            <h4>Lesmateriaal</h4>
                        </Card.Header>
                        <Card.Body className="p-0"> {/* Remove padding to prevent scrollbar cutoff */}
                            <div style={scrollableStyle} className="p-3"> {/* Add padding inside scrollable area */}
                                {lessonMaterials.map((material, index) => (
                                    <div key={`material-${material.textId}-${index}`} className="lesson-text mb-4">
                                        <div className="text-content" style={{ whiteSpace: 'pre-wrap' }}>
                                            {material.content}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card>
                        <Card.Header>
                            <h4>{currentLesson.title}</h4>
                        </Card.Header>
                        <Card.Body className="p-0"> {/* Remove padding to prevent scrollbar cutoff */}
                            <div style={scrollableStyle} className="p-3"> {/* Add padding inside scrollable area */}
                                <div className="lesson-content mb-4">
                                    <p>{currentLesson.content}</p>
                                </div>

                                {questions.map((question) => (
                                    <Card key={`question-${question.questionId}`} className="mb-4">
                                        <Card.Body>
                                            <h5>{question.questionText}</h5>
                                            <Form>
                                                {question.questionType === 'multiple_choice' ? (
                                                    question.answers.map((answer) => (
                                                        <Form.Check
                                                            key={`answer-${question.questionId}-${answer.answerId}`}
                                                            type="radio"
                                                            label={answer.answerText}
                                                            name={`question-${question.questionId}`}
                                                            onChange={() => handleAnswerSelection(
                                                                question.questionId,
                                                                answer.answerId,
                                                                answer.isCorrect
                                                            )}
                                                            checked={selectedAnswers[question.questionId] === answer.answerId}
                                                        />
                                                    ))
                                                ) : (
                                                    <Form.Control
                                                        as="textarea"
                                                        value={openAnswers[question.questionId] || ''}
                                                        onChange={(e) => handleOpenAnswerChange(question.questionId, e.target.value)}
                                                    />
                                                )}
                                            </Form>
                                            {answerFeedback[question.questionId] && (
                                                <div className="feedback mt-3">
                                                    <strong>{answerFeedback[question.questionId]}</strong>
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                ))}

                                {currentLesson && (
                                    <div className="d-flex justify-content-between mt-4 mb-4">
                                        <Button onClick={handlePreviousLesson} disabled={lessons.findIndex((lesson) => lesson.lessonId === currentLesson.lessonId) === 0} variant="secondary">
                                            Vorige les
                                        </Button>
                                        <Button onClick={handleLessonComplete}>
                                            {isLastLesson ? 'Voltooi cursus' : 'Volgende les'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default CourseView;
